import styled from "styled-components/macro";
import React, { TransitionStartFunction } from "react";
import * as d3 from "d3";
import { zoom as d3Zoom, D3ZoomEvent } from "d3-zoom";
import { connect } from "react-redux";
import type * as types from "../../../types/types";

import TimelineTooltip from "./TimelineTooltip/TimelineTooltip";
import NowIndicator from "./NowIndicator";
import {
  setVisibleRange,
  setTimeAxisState,
} from "../../../providers/GlobalStateProvider";

import {
  getIsSafari,
  ROW_HEIGHT,
  ROW_PADDING,
  TOP_PADDING,
  TOP_PADDING_FOCUS,
} from "../../../constants/constants";
import { locale, generateTimeZoneAwareDate } from "../../../utils/timeUtils";
import { tickGroups } from "./tickGroups";
import TimeAxisSvgStyles, { TICKS_TRANSLATE_Y } from "./TimeAxisStyles";
import { TimeRangeFinite } from "../../../types/types";
import ErrorBoundary from "../../SimpleComponents/Errors/ErrorBoundary";
import TimeAxisControls from "./TimeAxisControls";
import { isEqual, omit } from "lodash";
import ModalVertical from "./ModalVertical";
import {
  SliderSideEnum,
  updateRetrotagSegmentRange,
  updateSliderSideCurrentlyPressed,
} from "store/retrotagSlice";
import { RootState } from "store/store";
import { getTagHighlightHeightPlusPadding } from "../HubletView/SingleTimeline/TagHighlightTimelineBackdrop";
import LoadingSegmentsIndicator from "./LoadingSegmentsIndicator";
import { ShiftBoundariesIndicators } from "./ShiftBoundariesIndicators";
import { getIsTouchDevice } from "utils/getIsTouchDevice";
import { useAtom } from "jotai";
import { allTimelinesLoadedAtom } from "./ConnectedTimeline";
import { Portal } from "@mui/material";
import { trendChartHoveredRangeAtom } from "../TrendChart/TrendChartContent/TrendChartBar/TrendChartBar";
import { useTimeAxisContext } from "utils/customHooks/genericHooks";
import { getScaleFromWidthForLargeScreens } from "pages/DashboardPages/DashboardPagesContent/dashboardPagesContentUtils";
import { BREAKPOINT_DESKTOP_XL } from "components/MeritTier0Page/utils/tier0-utils";
const isSafari = getIsSafari();
const isPointerLockSupported =
  // temporarily disabling until timeline jump bug is resolved
  false &&
  !isSafari && // safari pointerlock causes a layout shift, it's not very nice
  ("pointerLockElement" in document ||
    "mozPointerLockElement" in document ||
    "webkitPointerLockElement" in document);
interface TimeAxisProps {
  width: number;
  startTransition: TransitionStartFunction;
  zoomable?: boolean;
  interactionTimeout: number;
  customerKey: string;
  range?: TimeRangeFinite | null;
  rangePadding: number;
  startSliding?: boolean;
  isGridLayoutPage?: boolean;
  isHubletPage?: boolean;
  isDarkMode?: boolean;
  isMultipleTimelinesPage?: boolean;
  isFocusRangeOutsideVisibleRange?: boolean;
  dispatch: any;
  children: any;
  timeZoneLocaleString: string;
  shouldUseCustomTimeZone: boolean;
  xLeft: types.TimeAxisContextStoreType["xLeft"];
  xRight: types.TimeAxisContextStoreType["xRight"];
  isSingleTimeline?: boolean;
  isRetrotagPopperOpen: boolean;
  sliderSideCurrentlyPressed: SliderSideEnum | null;
  retrotagSegmentRange: TimeRangeFinite | null;
  retrotagSegmentBoundingRange: TimeRangeFinite | null;
  showTagHighlights?: boolean;
  zoomTransform?: d3.ZoomTransform;
  isTakingSnapshot?: boolean;
  subTimelinesScrollPaddingBottom?: number;
}

interface TimeAxisState {
  zoomTransform: d3.ZoomTransform;
  epoch: number;
  lastInteraction: number;
  isSliding: boolean;
}

export class TimeAxis extends React.Component<TimeAxisProps, TimeAxisState> {
  public static defaultProps = {
    interactionTimeout: 60 * 1000,
    windowFudge: 10,
    rangePadding: 0.1,
  };

  state: TimeAxisState;
  private scaleTime = d3.scaleTime();
  private scaleTimeBase = d3.scaleTime();
  private timeAxisSVGRef = document.getElementById("timeAxisSvg");
  private timeAxisOverflowRef = document.querySelector(
    ".timeline-overflow-wrapper"
  );
  private timer?: number;
  private xLeft = 0;
  private xRight = 0;
  private tLeft = 0;
  private tRight = 0;
  private shouldSetVisibleRangeTo = null as null | { ge: number; lt: number };

  /** how much the user has dragged horizontally since last mousedown */
  private pendingTranslateX = 0;
  /** how much the user has dragged horizontally in total */
  private totalTranslateX = 0;
  private initialScreenX = 0;
  private pointerlockMovementX = 0;
  private pointerlockStarted = false;
  private pointerlockError = null as null | Error;
  private isValidPointerLockTarget = false;
  private prevZoomTransformK = null as null | number;

  /**
   * If a RetrotagSegmentSelector slider is being interacted with,
   * updateSliderSideCurrentlyPressed to disable other TimeAxis interaction
   */
  private zoomstart = async e => {
    // * bug: pointerLock causes timeline to jump if we start dragging from too far to the left or right (roughly where the focus range starts/ends on first page load)
    // * solution: disable pointerlock if we're too close to the left or right edge
    const rect = e.sourceEvent?.target?.getBoundingClientRect();
    const isFarEnoughFromLeft =
      e.sourceEvent?.clientX - rect?.left > POINTERLOCK_LIMIT_HZ;
    const isFarEnoughFromRight =
      rect?.right - e.sourceEvent?.clientX > POINTERLOCK_LIMIT_HZ;
    this.isValidPointerLockTarget = isFarEnoughFromLeft && isFarEnoughFromRight;

    this.pointerlockMovementX = 0;
    if (!this.props.isRetrotagPopperOpen) {
      this.timeAxisOverflowRef?.classList.add("dragging");
    }

    const sliderEvents = ["mousemove", "touchmove", "mousedown", "touchstart"];
    if (sliderEvents.includes(e?.sourceEvent?.type)) {
      const elementClassName = e.sourceEvent.target.className.animVal;
      if (
        elementClassName?.includes("retrotagSliderInteractionArea") ||
        elementClassName?.includes("retrotagSliderIndicator")
      ) {
        const sliderSide = elementClassName.includes(SliderSideEnum.left)
          ? SliderSideEnum.left
          : SliderSideEnum.right;
        this.props.dispatch(updateSliderSideCurrentlyPressed(sliderSide));
      }
    }
  };

  /**
   * Set sliderSideCurrentlyPressed back to null on zoom end to re-enable
   * TimeAxis functionality outside of the RetrotagSegmentSelector
   *
   * * this function gets called on mouseup and zoom
   */
  private zoomend = e => {
    this.pointerlockStarted = false;
    if (isPointerLockSupported) {
      document.exitPointerLock?.();
    }
    this.timeAxisOverflowRef?.classList.remove("dragging");

    // reset the sliderSideCurrentlyPressed
    if (this.props.sliderSideCurrentlyPressed) {
      this.props.dispatch(updateSliderSideCurrentlyPressed(null));

      // somehow, dragging retrotag slider unintentionally registers as a transform.x which causes undesirable timeline jump on the next drag/zoom, based on how much we dragged on the retrotag slider,
      // so, cancel it out here

      // dx = how much we moved horizontally
      const dx =
        // state.x / state.k = "normalized x position on mousedown"
        this.state.zoomTransform.x / this.state.zoomTransform.k -
        // event.x / event.k = "normalized x position on mouseup"
        e.transform.x / e.transform.k;
      // multiply by the current zoom level to get the actual distance
      this.pendingTranslateX = dx * e.transform.k - this.totalTranslateX;
    }
    // for mouseup, complete the "fake translate" via state, and reset the temporary transform-x
    if (
      e?.sourceEvent?.type === "mouseup" ||
      e?.sourceEvent?.type === "touchend"
    ) {
      // if we didn't drag, return
      if (!this.pendingTranslateX && !this.pointerlockMovementX) {
        return;
      }

      this.handleMouseUpOrZoom(e, false);
    }
  };

  private zoom = d3Zoom<SVGSVGElement, Record<string, unknown>>()
    .filter(event => {
      // Disable double-click zoom functionality
      if (event.type === "dblclick") {
        return false;
      }
      return true;
    })
    .scaleExtent([0.05 * (this.props.isSingleTimeline ? 0.6 : 1), 10000])
    .on("start", this.zoomstart)
    .on("end", this.zoomend)
    .on("zoom", async (e: ZoomEvent) => {
      const isWaitingForPointerlock =
        isPointerLockSupported &&
        document.pointerLockElement &&
        !this.pointerlockStarted &&
        !this.pointerlockError;
      if (isWaitingForPointerlock) {
        return;
      }

      // request pointer lock if we've moved more than 1 px horizontally
      if (
        // e.sourceEvent?.type === "mousedown" &&
        isPointerLockSupported &&
        this.isValidPointerLockTarget &&
        !this.pointerlockError &&
        Math.abs(this.pendingTranslateX) > 1
      ) {
        try {
          await this.timeAxisOverflowRef?.requestPointerLock();
          this.pointerlockStarted = true;
        } catch (error) {
          console.log("error requesting pointer lock - disabling pointerlock");
          console.log(error);
          this.pointerlockError = error as Error;
        }
      }

      if (e && e.sourceEvent) {
        // if pinching, don't zoom the whole page as well
        e.sourceEvent.stopPropagation();

        if (this.props.sliderSideCurrentlyPressed) {
          this.moveRetrotagSlider(e);
          // When the slider is moving, we won't want anything else in
          // the TimeAxis to change, so return
          return;
        }
        // if !zoomable, disable here, since we still want to enable retrotag bounds dragging (handled above)
        if (!this.props.zoomable) {
          return;
        }

        const isDragging = e.transform.k === this.state.zoomTransform.k;

        // If dragging determine if we should do zoom updates
        // or scroll the screen. The zoom updates are slow as
        // they cause a rerender of the whole timeline.
        if (isDragging) {
          // You can scroll past the edge of the page, need to saturate y
          const pendingDiffX = Math.abs(
            this.state.zoomTransform.x - e.transform.x
          );
          const pendingDiffK = Math.abs(
            this.state.zoomTransform.k - e.transform.k
          );
          const couldDoScrollVertically =
            // never scroll vertically on single-timeline page, grid page
            !this.props.isGridLayoutPage &&
            !this.props.isSingleTimeline &&
            pendingDiffX < 5 &&
            pendingDiffK === 0 &&
            Math.abs(-e.transform.y) > pendingDiffX;
          if (couldDoScrollVertically) {
            window.scrollBy(0, -e.transform.y);
          }
          this.handleHorizontalDrag(e);
        } else {
          // If we're not dragging, we're zooming.
          this.handleMouseUpOrZoom(e, true);
        }
      }
    });
  // To consider: should we check the magnitude of the zoom before calling
  // setState in order to reduce the number of renders required?

  constructor(props: TimeAxisProps) {
    super(props);
    this.state = {
      zoomTransform: this.props.zoomTransform ?? d3.zoomIdentity,
      epoch: Date.now(),
      lastInteraction: 0,
      isSliding: !!props.startSliding,
    };
  }

  /**
   *  horizontal dragging:
   *  don't set state (bad performance)
   *  instead just manually translate-x the svg ("fake translate")
   *  (we'll setState in handleMouseUpOrZoom)
   */
  private handleHorizontalDrag(e: ZoomEvent) {
    const screenX =
      e.sourceEvent?.screenX ??
      // on touch device, screenX is in touches[]
      e.sourceEvent.touches[e.sourceEvent.touches.length - 1]?.screenX ??
      0;

    // memorize initial screenX to determine movementX (event.movementX is not supported on ie11)
    if (!this.initialScreenX) {
      this.initialScreenX = screenX;
    }
    if (
      isPointerLockSupported &&
      this.pointerlockStarted &&
      !this.pointerlockError
    ) {
      // since pointerlock doesn't move the mouse, we can't use screenX, so need to track the movement separately
      this.pointerlockMovementX += e.sourceEvent.movementX;
      // totalTranslateX is what determines where the timeline ends up after mouseup
      this.totalTranslateX += e.sourceEvent.movementX;
    }
    const isVeryLargeScreen = this.props.width > BREAKPOINT_DESKTOP_XL;
    const scaleForLargeScreens = getScaleFromWidthForLargeScreens(
      this.props.width
    );
    const movementXBase =
      screenX -
      this.initialScreenX +
      (this.pointerlockStarted ? this.pointerlockMovementX : 0);

    let movementX = movementXBase;

    if (isVeryLargeScreen && !this.props.isSingleTimeline) {
      // on Timeline page + large screens, drag amount needs to be scaled up
      movementX = movementXBase * scaleForLargeScreens ** 1.54;
    }

    // transform the timeline & tooltip
    this.timeAxisSVGRef?.setAttribute(
      "transform",
      `translate(${movementX}, 0)`
    );
    if (isSafari && this.timeAxisSVGRef) {
      this.timeAxisSVGRef.style.transform = `translate(${movementX}px, 0)`;
    }

    // while dragging, transform the tooltip-wrapper so it follows the mouse,
    // then on mouseup (in handleMouseUpOrZoom), reset tooltip-wrapper transform when tooltip gets re-rendered at new position
    const tooltipWrapper =
      document.querySelector<HTMLDivElement>(`.tooltipWrapper`);
    if (tooltipWrapper) {
      tooltipWrapper.style.transform = `translate(${movementX}px, 0)`;
    }

    this.pendingTranslateX = movementX;
  }

  /** only setState on mouseup/zoom, not on drag (better performance) */
  private handleMouseUpOrZoom = (e: ZoomEvent, isZoom) => {
    const tooltipWrapper =
      document.querySelector<HTMLDivElement>(`.tooltipWrapper`);
    if (tooltipWrapper) {
      tooltipWrapper.style.transform = `translate(${0}px, 0)`;
    }

    this.pointerlockMovementX = 0;

    // on touch devices, startTransition interrupts the pinch midway, which turns it into a zoom. So, don't startTransition on touch devices
    const isTouchDevice = getIsTouchDevice();
    const startTransitionNonTouchOnly = isTouchDevice
      ? cbFn => {
          cbFn();
          this.timeAxisSVGRef?.setAttribute("transform", `translate(0, 0)`);
        }
      : this.props.startTransition;
    startTransitionNonTouchOnly(() => {
      // totalTranslateX scales with zoom
      if (isZoom) {
        const zoomRatio =
          e.transform.k /
          (this.prevZoomTransformK ?? this.state.zoomTransform.k);
        // need to instantly track previous zoom level to prevent double zooms (causes timeline jump)
        this.prevZoomTransformK = e.transform.k;
        this.totalTranslateX = this.totalTranslateX * zoomRatio;
      }
      // tally up and reset the pendingTranslateX from the last drag
      this.totalTranslateX += this.pendingTranslateX;
      this.pendingTranslateX = 0;
      // reset initialScreenX for next drag
      this.initialScreenX = 0;

      const nextTransform = e.transform.translate(
        this.getEpochDistance() + this.totalTranslateX / e.transform.k,
        0
      );

      this.setState({
        zoomTransform: nextTransform,
        lastInteraction: Date.now(),
      });
      if (isSafari && this.timeAxisSVGRef) {
        this.timeAxisSVGRef.style.transform = "translate(0, 0)";
      }
    });
  };

  private getEpochDistance = () => {
    const { epoch, isSliding } = this.state;
    // If the view is currently sliding we need to account
    // for the amount of that has passed when calculating
    // what the new zoomTransform should be.
    return isSliding
      ? (this.scaleTimeBase(epoch) || 0) - (this.scaleTimeBase(Date.now()) || 0)
      : 0;
  };

  /**
   * Update the ge and lt of the currently selected RetrotagSegment as
   * one of the sliders move
   */
  private moveRetrotagSlider(event: any) {
    const element = document.getElementById("timeAxisSvg");
    const mousePos = element ? d3.pointer(event, element) : null;
    const {
      retrotagSegmentRange: retrotagRange,
      retrotagSegmentBoundingRange: retrotagBoundingRange,
    } = this.props;

    if (mousePos && retrotagRange && retrotagBoundingRange) {
      const [x] = mousePos;
      const t = this.scaleTime.invert(x).getTime();
      const newRetrotagGe =
        this.props.sliderSideCurrentlyPressed === SliderSideEnum.left
          ? Math.max(t, retrotagBoundingRange.ge)
          : retrotagRange.ge;
      const newRetrotagLt =
        this.props.sliderSideCurrentlyPressed === SliderSideEnum.right
          ? Math.min(t, retrotagBoundingRange.lt)
          : retrotagRange.lt;

      if (newRetrotagGe < newRetrotagLt) {
        this.props.dispatch(
          updateRetrotagSegmentRange({
            ge: newRetrotagGe,
            lt: newRetrotagLt,
          })
        );
      }
    }
  }

  componentWillUnmount() {
    clearInterval(this.timer);
  }

  componentDidUpdate(prevProps: TimeAxisProps, prevState: TimeAxisState) {
    const { dispatch } = this.props;
    const geNow = this.props.range && this.props.range.ge;
    const ltNow = this.props.range && this.props.range.lt;
    const gePrev = prevProps.range && prevProps.range.ge;
    const ltPrev = prevProps.range && prevProps.range.lt;
    // When the range prop updates we may need to flip into
    // focus mode from sliding. If we are in sliding mode,
    // and sliding mode is still valid (e.g., shift change),
    // we want to stay in sliding mode. Otherwise, flip intp
    // focus mode (e.g., because the user clicked back to
    // look at previous shifts, or clicked forward while
    // focus wasn't aligned).
    if (
      // Check if we were sliding, but can no longer slide
      // (e.g., stepped backwards), or if we are not sliding,
      // in which case we might as well reset focus.
      ((this.state.isSliding && !this.isSlideValid(ltNow)) ||
        !this.state.isSliding) &&
      // The rest checks for a change is visible range.
      geNow &&
      ltNow &&
      gePrev &&
      ltPrev &&
      (geNow !== gePrev || ltNow !== ltPrev)
    ) {
      this.resetToFocus(false);
    }

    const [domainStart, domainEnd] = this.scaleTime.domain();

    // update the TimeAxisContextProvider when we update the TimeAxis state
    const nextTimeAxisContext = {
      scaleTimeDomain: [domainStart.getTime(), domainEnd.getTime()] as [
        number,
        number,
      ],
      scaleTimeRange: this.scaleTime.range() as [number, number],
      xLeft: this.xLeft,
      xRight: this.xRight,
      zoomTransform: this.state.zoomTransform,
    };

    if (
      this.props.xLeft !== nextTimeAxisContext.xLeft ||
      this.props.xRight !== nextTimeAxisContext.xRight ||
      prevProps.width !== this.props.width ||
      !isEqual(prevState, this.state) ||
      !isEqual(prevProps.range, this.props.range)
    ) {
      const isTouchDevice = getIsTouchDevice();
      const startTransitionNonTouchOnly = isTouchDevice
        ? cbFn => cbFn()
        : this.props.startTransition;
      startTransitionNonTouchOnly(() => {
        dispatch(setTimeAxisState(nextTimeAxisContext));
      });
    }

    if (this.shouldSetVisibleRangeTo) {
      dispatch(setVisibleRange(this.shouldSetVisibleRangeTo));
      this.shouldSetVisibleRangeTo = null;

      // when TimeAxis re-renders due to prop change (like resizing window), we need to preserve zoomTransform, else it resets to d3.zoomIdentity on the next zoom/drag
      if (
        this.timeAxisSVGRef &&
        this.state.zoomTransform &&
        !isEqual(omit(prevProps, "children"), omit(this.props, "children"))
      ) {
        d3.select(this.timeAxisSVGRef).call(
          this.zoom.transform as any,
          this.state.zoomTransform
        );
      }
    }
  }

  isSlideValid = lt => Date.now() <= lt;

  getScaleTimeBase = (ge: number, lt: number, slidingWindow: number) => {
    let scaleTimeBase;
    if (this.isSlideValid(lt) && this.state.isSliding) {
      scaleTimeBase = d3
        .scaleTime()
        .domain([this.state.epoch - slidingWindow, this.state.epoch])
        .range([this.xLeft, this.xRight]);
      if (!this.timer) {
        const msPerPixel = slidingWindow / (this.xRight - this.xLeft);
        this.timer = window.setInterval(
          () => {
            const now = Date.now();
            if (
              now - this.state.lastInteraction >
              this.props.interactionTimeout
            ) {
              // When there is no recent interaction keep the zoom moving along
              // and offset by how much time has passed since the timeline epoch.
              // We add a little fudge onto now in order to put it slightly into
              // the future so we don't end up clipping things that are happening
              // right now.
              this.resetToSliding(false);
            }
            // When there has been recent interaction, don't mess with the zoom
          },
          // Update somewhat faster than whole pixel
          Math.max(200, 0.3 * msPerPixel)
        );
      }
    } else {
      clearInterval(this.timer);
      this.timer = undefined;
      scaleTimeBase = d3
        .scaleTime()
        .domain([ge, lt])
        .range([this.xLeft, this.xRight]);
    }
    return scaleTimeBase;
  };

  resetToSliding = replaceLastInteraction => {
    // wait to get the ref
    if (this.timeAxisSVGRef !== undefined) {
      const now = Date.now();
      const { interactionTimeout } = this.props;
      const { epoch } = this.state;
      (d3.select(this.timeAxisSVGRef) as any).call(
        this.zoom.transform,
        d3.zoomIdentity
      );
      const epochDistance =
        (this.scaleTimeBase(epoch) || 0) - (this.scaleTimeBase(now) || 0);

      this.totalTranslateX = 0;

      // IE11 doesn't like the more compact way of adding conditional
      // fields so be more verbose with the setState alternatives.
      if (replaceLastInteraction) {
        this.setState({
          zoomTransform: d3.zoomIdentity.translate(epochDistance, 0),
          isSliding: true,
          lastInteraction: now - interactionTimeout,
        });
      } else {
        this.setState({
          zoomTransform: d3.zoomIdentity.translate(epochDistance, 0),
          isSliding: true,
        });
      }
    }
  };

  /** reset the timeline zoom & pan (zoomTransform & visibleRange)
   * to the current focusRange (i.e. shift or 24hrs)
   */
  resetToFocus = replaceLastInteraction => {
    // wait to get the ref
    if (this.timeAxisSVGRef !== undefined) {
      this.totalTranslateX = 0;

      const now = Date.now();

      // ? reset the zoom to the default ("not zoomed") value
      (d3.select(this.timeAxisSVGRef) as any).call(
        this.zoom.transform,
        d3.zoomIdentity
      );

      // reset the zoomTransform, and set isSliding: false

      // IE11 doesn't like the more compact way of adding conditional
      // fields so be more verbose with the setState alternatives.
      if (replaceLastInteraction) {
        this.setState({
          zoomTransform: d3.zoomIdentity,
          isSliding: false,
          lastInteraction: now,
        });
      } else {
        this.setState({
          zoomTransform: d3.zoomIdentity,
          isSliding: false,
        });
      }
    }
  };

  getChildren = () => {
    // Row spacing and padding
    const initialOffset = TOP_PADDING + ROW_PADDING - ROW_HEIGHT;
    let currentOffset = initialOffset;
    const offsets = [initialOffset];
    const processIds: string[] = [];
    const children = (
      <g transform={`translate(0, ${ROW_HEIGHT * 0.5})`}>
        {React.Children.map(this.props.children, (child, idx) => {
          const showTagHighlight = this.props.showTagHighlights && idx > 0;
          const tagHighlightHeightPlusPadding =
            getTagHighlightHeightPlusPadding(
              showTagHighlight,
              this.props.isHubletPage,
              this.props.isMultipleTimelinesPage
            );

          currentOffset =
            currentOffset +
            ROW_HEIGHT +
            ROW_PADDING +
            tagHighlightHeightPlusPadding;
          offsets.push(currentOffset);
          const clonedChild = React.cloneElement(
            child as React.ReactElement<any>,
            { offset: currentOffset }
          );
          processIds.push(clonedChild.props.process.id);

          return (
            <>
              <TimeAxisLoadingSegmentsIndicator
                {...{
                  timelineIdx: idx,
                  currentOffset,
                }}
              />
              {clonedChild}
            </>
          );
        })}
      </g>
    );
    const height = currentOffset + ROW_HEIGHT * 1.5; // add 0.5 row for NowIndicator
    return { height, offsets, children, processIds };
  };

  setupClicks = (timeAxisSVGRef, offsets) => {
    // save the time axis ref to the private value this.timeAxisSVGRef
    this.timeAxisSVGRef = timeAxisSVGRef;
    (d3.select(timeAxisSVGRef) as any).call(this.zoom);
  };

  render() {
    const {
      width,
      range,
      rangePadding,
      timeZoneLocaleString,
      shouldUseCustomTimeZone,
    } = this.props;

    if (!range || !width) {
      return null;
    }

    const { zoomTransform: zt, lastInteraction, isSliding } = this.state;
    this.xLeft = 0;
    this.xRight = width;

    const rangeZoned = {
      ge: generateTimeZoneAwareDate(
        shouldUseCustomTimeZone,
        range.ge,
        timeZoneLocaleString
      ).getTime(),
      lt: generateTimeZoneAwareDate(
        shouldUseCustomTimeZone,
        range.lt,
        timeZoneLocaleString
      ).getTime(),
    };

    const slidingWindow = rangeZoned.lt - rangeZoned.ge;
    const ge = range.ge - rangePadding * slidingWindow;
    const lt = range.lt + rangePadding * slidingWindow;

    // Need to do some manual work for the zoom to keep as much of
    // the d3 code declarative (so no letting d3 apply the transforms).
    // First we get the original scale, this is what d3 reports the
    // zoom relative to. From the original scale we manually translate
    // and scale the original range [0, width] according to the current
    // zoom and then we calcuale the current domain and time scale.
    // React then handles the diffing and rendering.
    this.scaleTimeBase = this.getScaleTimeBase(ge, lt, slidingWindow);
    const t0 = this.scaleTimeBase.invert((this.xLeft - zt.x) / zt.k).getTime();
    const t1 = this.scaleTimeBase.invert((this.xRight - zt.x) / zt.k).getTime();

    this.scaleTime = d3
      .scaleTime()
      .domain([t0, t1])
      .range([this.xLeft, this.xRight]);

    if (t0 !== this.tLeft || t1 !== this.tRight) {
      this.tLeft = t0;
      this.tRight = t1;
      this.shouldSetVisibleRangeTo = { ge: t0, lt: t1 };
    }

    const t0zoned = generateTimeZoneAwareDate(
      shouldUseCustomTimeZone,
      t0,
      timeZoneLocaleString
    );
    const t1zoned = generateTimeZoneAwareDate(
      shouldUseCustomTimeZone,
      t1,
      timeZoneLocaleString
    );
    const t0ZonedTime = t0zoned.getTime();
    const t1zonedTime = t1zoned.getTime();

    const { height, offsets, children, processIds } = this.getChildren();
    const now = generateTimeZoneAwareDate(
      shouldUseCustomTimeZone,
      Date.now(),
      timeZoneLocaleString
    ).getTime();
    const isSlidingAttached =
      isSliding &&
      lt &&
      now <= lt &&
      now - lastInteraction > this.props.interactionTimeout;

    const tagHighlightHeight = getTagHighlightHeightPlusPadding(
      this.props.showTagHighlights,
      false,
      this.props.isMultipleTimelinesPage
    );

    const numChildren = React.Children.count(this.props.children);
    const rowHeight = ROW_HEIGHT + ROW_PADDING + tagHighlightHeight;
    const heightOfRows = numChildren * rowHeight;

    const visibleRangeMs = t1zonedTime - t0ZonedTime;
    return (
      <>
        <ErrorBoundary id="TimeAxis">
          <OverflowWrapperStyles
            id="timeAxisOverflowWrapper"
            className={`timeline-overflow-wrapper${
              this.props.isMultipleTimelinesPage
                ? " multiple-timelines-page"
                : ""
            }`}
            ref={ref => (this.timeAxisOverflowRef = ref)}
          >
            <TimeAxisSvgStyles
              $isDarkMode={this.props.isDarkMode}
              $isTakingSnapshot={this.props.isTakingSnapshot}
              $isSingleTimeline={this.props.isSingleTimeline}
              $subTimelinesScrollPaddingBottom={
                this.props.subTimelinesScrollPaddingBottom
              }
              className="timeAxisSvg"
              id="timeAxisSvg"
              data-component-name={"Time Axis"}
              width={width}
              height={height}
              ref={(timeAxisSVGRef: SVGSVGElement) => {
                if (timeAxisSVGRef) {
                  this.setupClicks(timeAxisSVGRef, offsets);
                }
              }}
            >
              {children}
              <FocusBlocks
                {...{
                  focusRange: range,
                  height: height + tagHighlightHeight,
                  scaleTime: this.scaleTime,
                  xLeft: this.xLeft,
                  xRight: this.xRight,
                }}
              />
              <HoveredRangeHighlightIndicator height={heightOfRows} />
              <TickGroups
                {...{
                  t0: t0ZonedTime,
                  t1: t1zonedTime,
                  side: "visible",
                  height: heightOfRows,
                  range,
                  xLeft: this.xLeft,
                  xRight: this.xRight,
                }}
              />
              <ShiftBoundariesIndicators {...{ height }} />
              <OverflowLeftRightTickGroups
                {...{
                  t0ZonedTime,
                  visibleRangeMs,
                  t1zonedTime,
                  heightOfRows,
                  width,
                  range,
                  xLeft: this.xLeft,
                  xRight: this.xRight,
                }}
              />
              <NowIndicator
                height={heightOfRows}
                location={isSlidingAttached ? this.xRight : null}
              />
              <ModalVertical {...{ height }} />
            </TimeAxisSvgStyles>
          </OverflowWrapperStyles>
        </ErrorBoundary>
        <TimeAxisControls
          isSingleTimeline={Boolean(this.props.isSingleTimeline)}
          isSlidingAttached={Boolean(isSlidingAttached)}
          lt={lt}
          state={this.state}
          resetToFocus={this.resetToFocus}
          resetToSliding={this.resetToSliding}
          isSlideValid={this.isSlideValid}
        />
        {!this.props.isRetrotagPopperOpen && (
          <ErrorBoundary id="TimelineTooltip">
            <Portal
              container={document.getElementById("timeAxisOverflowWrapper")}
            >
              <TimelineTooltip
                {...{
                  offsets,
                  processIds,
                  isSingleTimeline: Boolean(this.props.isSingleTimeline),
                }}
              />
            </Portal>
          </ErrorBoundary>
        )}
      </>
    );
  }
}

const mapStateToProps = ({
  legacy: { focusRange, visibleRange, timeAxisContext },
  retrotag: {
    isRetrotagPopperOpen,
    sliderSideCurrentlyPressed,
    retrotagSegmentRange,
    retrotagSegmentBoundingRange,
  },
}: RootState) => {
  const isFocusRangeOutsideVisibleRange = !!(
    focusRange &&
    visibleRange &&
    (visibleRange.ge < focusRange.ge - (focusRange.lt - focusRange.ge) ||
      visibleRange.lt > focusRange.lt + (focusRange.lt - focusRange.ge))
  );
  return {
    range: focusRange,
    isFocusRangeOutsideVisibleRange,
    xLeft: timeAxisContext.xLeft,
    xRight: timeAxisContext.xRight,
    isRetrotagPopperOpen,
    sliderSideCurrentlyPressed,
    retrotagSegmentRange,
    retrotagSegmentBoundingRange,
  };
};
export default connect(mapStateToProps)(TimeAxis);

const OverflowWrapperStyles = styled.div`
  position: relative;
  overflow: visible;
  .overflow-left,
  .overflow-right,
  .overflow-ticks {
    display: none;
  }
  &.dragging {
    .loading-segments-indicator,
    .overflow-left,
    .overflow-right,
    .overflow-ticks {
      display: block;
    }
  }
`;
type ZoomEvent = D3ZoomEvent<SVGSVGElement, Record<string, unknown>>;

function TimeAxisLoadingSegmentsIndicator({ timelineIdx, currentOffset }) {
  const [allTimelinesLoaded] = useAtom(allTimelinesLoadedAtom);
  const thisTimelineLoaded = allTimelinesLoaded[timelineIdx];
  return thisTimelineLoaded ? null : (
    <g transform={`translate(0, ${currentOffset})`}>
      <LoadingSegmentsIndicator
        {...{
          timelineIdx,
          height: ROW_HEIGHT,
          machemetryId: "",
        }}
      />
    </g>
  );
}

function TickGroups({
  t0,
  t1,
  height,
  translateX = 0,
  className = "",
  range,
  xLeft,
  xRight,
}: {
  t0: number;
  t1: number;
  height: number;
  translateX?: number;
  className?: string;
  range: TimeRangeFinite | null;
  xLeft: number;
  xRight: number;
}) {
  const tg = tickGroups(t1 - t0);
  return (
    <>
      {tg.map(({ nTicks, tickFormat, classNames }) => {
        if (!range) {
          return;
        }
        return (
          <g
            key={classNames}
            ref={(tickGroupRef: SVGGElement) =>
              // ? select the tick group
              d3.select(tickGroupRef).call(
                // create a "ticks" axis on top of it
                d3
                  .axisTop(
                    d3.scaleTime().domain([t0, t1]).range([xLeft, xRight])
                  )
                  .tickSize(ROW_PADDING - height)
                  .tickPadding(ROW_PADDING)
                  .ticks(nTicks)
                  .tickFormat(d => locale.format(tickFormat)(d as Date))
              )
            }
            className={`timescale ${classNames} ${className}`}
            transform={`translate(${translateX}, ${TICKS_TRANSLATE_Y})`}
          />
        );
      })}
    </>
  );
}

function FocusBlocks({
  focusRange,
  height,
  scaleTime,
  xLeft,
  xRight,
}: {
  focusRange: TimeRangeFinite | null;
  height: number;
  scaleTime: d3.ScaleTime<number, number>;
  xLeft: number;
  xRight: number;
}) {
  if (focusRange) {
    const xLeftFocus = Math.max(scaleTime(focusRange.ge) || 0, xLeft);
    const xRightFocus = Math.min(scaleTime(focusRange.lt) || 0, xRight);
    if (xLeftFocus < xRightFocus) {
      const pointsLeft: [number, number][] = [
        [xLeftFocus, TOP_PADDING_FOCUS], // top right
        [xLeft, TOP_PADDING_FOCUS], // top left
        [xLeft, height], // bottom left
        [xLeftFocus, height], // bottom right
      ];
      const pointsRight: [number, number][] = [
        [xRight, TOP_PADDING_FOCUS], // top right
        [xRightFocus, TOP_PADDING_FOCUS], // top left
        [xRightFocus, height], // bottom left
        [xRight, height], // bottom right
      ];
      // two blocks, on either side, if they have non-zero length
      const areaPathLeft = d3.line()(pointsLeft);
      const areaPathRight = d3.line()(pointsRight);
      return (
        <g className="outOfFocusGroup">
          <path
            className="outOfFocusPath outOfFocusPathLeft"
            d={areaPathLeft ? areaPathLeft : ""}
          />
          <path
            className="outOfFocusPath outOfFocusPathRight"
            d={areaPathRight ? areaPathRight : ""}
          />
        </g>
      );
    }
  }
  return null;
}
/** how many timeline-widths of ticks to show on left/right while dragging */
const NUM_TICKS_OVERFLOW = 24; // average shift = 8 hours, so allow 24 * 8h = ~1 week scrolling (ticks are very cheap to render, <0.1ms)

function OverflowLeftRightTickGroups({
  t0ZonedTime,
  visibleRangeMs,
  t1zonedTime,
  heightOfRows,
  width,
  range,
  xLeft,
  xRight,
}: {
  t0ZonedTime: number;
  visibleRangeMs: number;
  t1zonedTime: number;
  heightOfRows: number;
  width: number;
  range: TimeRangeFinite | null;
  xLeft: number;
  xRight: number;
}) {
  return (
    <>
      {[...Array(NUM_TICKS_OVERFLOW)].map((_, i) => (
        <TickGroups
          key={i}
          {...{
            t0: t0ZonedTime - visibleRangeMs * (i + 1),
            t1: t1zonedTime - visibleRangeMs * (i + 1),
            className: `overflow-ticks overflow-left overflow-left-${i + 1}`,
            height: heightOfRows,
            translateX: -width * (i + 1),
            range,
            xLeft: xLeft,
            xRight: xRight,
          }}
        />
      ))}
      {[...Array(NUM_TICKS_OVERFLOW)].map((_, i) => (
        <TickGroups
          key={i}
          {...{
            t0: t0ZonedTime + visibleRangeMs * (i + 1),
            t1: t1zonedTime + visibleRangeMs * (i + 1),
            className: `overflow-ticks overflow-right overflow-right-${i + 1}`,
            height: heightOfRows,
            translateX: width * (i + 1),
            range,
            xLeft: xLeft,
            xRight: xRight,
          }}
        />
      ))}
    </>
  );
}

/** pointerlock bug: if starting drag from the edges of the timeline, timeline jumps in the drag direction
 * so, we limit the pointerlock to a certain distance from the edges
 */
const POINTERLOCK_LIMIT_HZ = 172;

/** when hovering over a TrendChartBar, we highlight associated time range */
function HoveredRangeHighlightIndicator({ height }: { height: number }) {
  const [trendChartHoveredRange] = useAtom(trendChartHoveredRangeAtom);
  const { scaleTime } = useTimeAxisContext();
  if (!trendChartHoveredRange) {
    return null;
  }
  // a white transparent overlay bar that highlights the hovered range
  return (
    <rect
      className="hoveredRangeHighlightIndicator"
      fill="rgba(255, 255, 255, 0.3)"
      x={scaleTime(trendChartHoveredRange.ge)}
      y={32}
      width={
        scaleTime(trendChartHoveredRange.lt) -
        scaleTime(trendChartHoveredRange.ge)
      }
      height={height}
    />
  );
}
